import { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useSchoolContext,
  useGenderedSportList,
  useUserContext,
} from "../../../contexts/ProviderProvider";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import { VENDORS } from "../../../utilities/utils";
import {
  Autocomplete,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import AthleteSearch from "./AthleteSearch";
import { createProducts } from "../../../API/products";
import useNotification from "../../../hooks/useNotification";
import {
  findProductMetadataMatching,
  getAllProductConfigurations,
} from "../../../API/productConfiguration";
import { ManufacturingData } from "./ConfigureNewProduct";
import JerseyNumberSelector from "./JerseyNumberSelector";
import MatchingSimilarProducts from "./MatchingSimilarProducts";
import MultiSelect from "../../common/MultiSelect";
import { NewProductEQMConfig } from "./NewProductEQMConfig";
import { getGeneralEQMAssetsByTypeForSku } from "../../../API/equipmentManager";
import { EQM_TAG_VERSION_1_SUFFIX } from "../../../constants/eqmTags";

const buildSKU = (color, unique, type) => {
  const format = (str) => {
    if (str && str !== "") {
      return str + " ";
    }
    if (str === undefined || str === null || str === "") {
      return "";
    }
  };
  if (color && type) {
    return `${color} ${format(unique)}${type}`;
  }
  return "";
};

const GetColorOptions = (productConfiguration, type) => {
  if (type && productConfiguration && productConfiguration[type]) {
    return Object.keys(productConfiguration[type]);
  }
  return [];
};

const GetConfiguration = (productConfiguration, type, color, selectedProductConfiguration, setSelectedProductConfiguration, onClick) => {

  const [hovering, setHovering] = useState();

  if (type && color) {
    if (productConfiguration[color] && productConfiguration[color][type]) {
      return (
        <div
          style={{
            display: "flex",
            direction: "row",
            gap: "1rem",
          }}
        >
          {productConfiguration[color][type].map((matching) => (
            <div
              style={{
                border:
                  selectedProductConfiguration?.product_config_id ===
                    matching.product_config_id
                    ? "solid green 2px"
                    : hovering?.product_config_id === matching.product_config_id
                      ? "solid gray 2px"
                      : "none",
              }}
              id={matching.product_config_id}
              onMouseOver={() => {
                setHovering(matching);
              }}
              onMouseLeave={() => {
                setHovering();
              }}
              onClick={() => {
                onClick(matching);
                setSelectedProductConfiguration(matching);
              }}
            >
              <ManufacturingData
                color={matching.color_reference}
                size={matching.size_range}
                style={matching.style_reference}
              />
            </div>
          ))}
        </div>
      );
    }
    return undefined;
  }
  return undefined;
};

const ErrorText = ({ message }) => {
  return <p style={{ color: "#f44336", fontSize: "0.75rem" }}>{message}</p>;
};

const filterVendors = (inputProdType) => {
  var TP = ["Coaster", "Jersey", "Blanket", "Tumbler", "Number Necklace"];
  var full = VENDORS;
  if (TP.includes(inputProdType)) {
    return full.filter((ven) => ven != "Influxer Merch");
  }

  return full;
};

const CreateProductForm = () => {
  const universities = useSchoolContext().schools?.map((s) => s.schoolName);
  const [productConfiguration, setProductConfigurations] = useState();
  const [similarProducts, setSimilarProducts] = useState([]);
  const [typeCheck, setTypeCheck] = useState();
  const genderedSportList = useGenderedSportList();
  // FOR EQM CONFIG
  const [rows, setRows] = useState([])
  const [errorMessage, setErrorMessage] = useState("");
  const [displayEQMConfigTab, setDisplayEQMConfigTab] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [generalAssetByType, setGeneralAssetByType] = useState({});
  const [fontOptions, setFontOptions] = useState([]);
  const [compositeOptions, setCompositeOptions] = useState([]);
  const [backIconLinkOptions, setBackIconLinkOptions] = useState([]);
  const [frontIconLinkOptions, setFrontIconLinkOptions] = useState([]);
  const [templateLinkOptions, setTemplateLinkOptions] = useState([]);
  const [jerseyTemplateLinkOptions, setJerseyTemplateLinkOptions] = useState([]);
  const [selectedProductConfiguration, setSelectedProductConfiguration] = useState();

  const user = useUserContext();
  const notify = useNotification();

  const [loading, setLoading] = useState(false);

  // Transform EQM config to match UniversityConfig interface
  const transformEQMConfigToUniversityConfig = (eqmConfig, universities) => {
    const transformedConfig = {};

    universities.forEach(university => {
      const universityConfig = eqmConfig[university] || {};

      // Derive boolean flags from conditional fields
      const hasFrontSportText = !!(universityConfig.front_text_font_link_general_asset_id || universityConfig.front_text_font_color);
      const hasBack = universityConfig.has_back !== undefined ? !!universityConfig.has_back : false;
      const hasMockupTemplateLink = !!universityConfig.mockup_template_general_asset_id;

      transformedConfig[university] = {
        equipment_manager_config_id: 0, // Will be set by backend
        metadata_id: 0, // Will be set by backend
        front_icon_general_asset_id: universityConfig.front_icon_general_asset_id || null,
        has_front_sport_text: hasFrontSportText,
        front_text_font_link_general_asset_id: universityConfig.front_text_font_link_general_asset_id || null,
        front_text_font_color: universityConfig.front_text_font_color || null,
        front_verified: universityConfig.front_verified || false,
        back_text_font_color: universityConfig.back_text_font_color || null,
        back_jersey_number_font_color: universityConfig.back_jersey_number_font_color || null,
        back_number_outline_color: universityConfig.back_number_outline_color || null,
        has_back: hasBack,
        back_icon_general_asset_id: universityConfig.back_icon_general_asset_id || null,
        back_text_font_general_asset_id: universityConfig.back_text_font_general_asset_id || null,
        back_jersey_number_font_general_asset_id: universityConfig.back_jersey_number_font_general_asset_id || null,
        back_verified: universityConfig.back_verified || false,
        has_mockup_template_link: hasMockupTemplateLink,
        mockup_template_general_asset_id: universityConfig.mockup_template_general_asset_id || null,
        mockup_verified: universityConfig.mockup_verified || false,
      };
    });

    console.log("asdf transformed")

    return transformedConfig;
  };

  const validationSchema = Yup.object().shape({
    skuName: Yup.string(),
    color: Yup.string()
      .required("Color is required")
      .test(
        "verify value in list",
        "select a value from the dropdown",
        (value) => {
          return Object.keys(productConfiguration).includes(value);
        }
      ),
    unique: Yup.string(),
    type: Yup.string().required("Type is required"),
    university: Yup.array().test(
      "verify not empty array",
      "you must select some schools, but no more than 4... for now",
      (value) =>
        value && Array.isArray(value) && value.length > 0 && value.length < 5
    ),
    sport: Yup.array().test(
      "verify not empty array",
      "you must select some sports, or all of them",
      (value) => value && Array.isArray(value) && value.length > 0
    ),
    jersey_number: Yup.array(),
    athleteId: Yup.number(),
    gender: Yup.array().test(
      "verify not empty array",
      "you must select a gender, or all of them",
      (value) =>
        value && Array.isArray(value) && value.length > 0 && value.length < 2
    ), // if you want both genders, select all
    approvalStatus: Yup.boolean().oneOf(
      [true, false],
      "Approval Status is required"
    ),
    vendor: Yup.string().required("Vendor is required"),
    colorReference: Yup.string(),
    styleReference: Yup.string(),
    sizeRange: Yup.string(),
    config: Yup.object().test(
      "verify university configs",
      "all selected universities must have complete EQM configurations",
      function (value) {
        if (!displayEQMConfigTab) return true;
        if (!value || Object.keys(value).length === 0) return false;

        // Check if all universities have required fields
        const universities = this.parent.university || [];
        for (const university of universities) {
          const universityConfig = value[university];
          if (!universityConfig) return false;

          if (!universityConfig.mockup_template_general_asset_id ||
            universityConfig.mockup_template_general_asset_id === "") {
            return false;
          }
        }
        return true;
      }
    ),
  });

  const handleSubmit = async (obj, { resetForm }) => {
    // Validate all universities' configurations
    if (displayEQMConfigTab) {
      if (!validateForm()) {
        setErrorMessage("* Missing required fields");
        return;
      }
    }

    let formObj = { ...obj };
    if (
      formObj.university.includes("All") ||
      formObj.university === undefined ||
      formObj.university.length === 0
    ) {
      delete formObj.university;
    }
    if (
      formObj.gender.includes("All") ||
      formObj.gender === undefined ||
      formObj.gender.length === 0
    ) {
      delete formObj.gender;
    } else {
      formObj.gender = formObj.gender[0];
    }
    if (formObj.athleteId === "" || formObj.athleteId === undefined) {
      delete formObj.athleteId;
    }
    if (
      formObj.sport.includes("All") ||
      formObj.sport === undefined ||
      formObj.sport.length === 0
    ) {
      delete formObj.sport;
    } else {
      formObj.genderedSport = formObj.sport;
      delete formObj.sport;
    }
    if (formObj.jersey_number[0] !== -2) {
      if (formObj.jersey_number[0] === -1) {
        formObj.jersey_number[0] = "00";
      }
      formObj.jersey_number = formObj.jersey_number[0] + "-" + formObj.jersey_number[1];
    } else {
      delete formObj.jersey_number;
    }

    // Transform the config to match UniversityConfig interface
    if (displayEQMConfigTab && formObj.config) {
      formObj.config = transformEQMConfigToUniversityConfig(formObj.config, formObj.university);
    }

    if (!formObj.config.has_back) {
      Object.keys(formObj).forEach(k => {
        if (k.includes("back_")) {
          formObj[k] = undefined
        }
      })
    }

    console.log('asdf formObject to pass to GS: ', formObj)

    setLoading(true);

    try {
      const response = await createProducts(formObj, user)
      console.log('response from player product creation: ', response)
    } catch (error) {
      console.error("error creating products:", error)
      notify("Failed to create product");
    } finally {
      setLoading(false);
      setSelectedProductConfiguration({});
      resetForm();
    }
  };

  const formik = useFormik({
    initialValues: {
      skuName: "",
      gender: [],
      color: "",
      type: "",
      unique: "",
      university: [],
      sport: [],
      jersey_number: [-2, -1],
      athleteId: undefined,
      approvalStatus: false,
      vendor: "",
      sizeRange: "",
      styleReference: "",
      colorReference: "",
      config: {},
      group: "Individual"
    },
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
  });

  // Create a dedicated function to update only the config field
  const updateEQMConfig = useCallback((configUpdater) => {
    if (typeof configUpdater === 'function') {
      // If it's a function, call it with the current config value
      const newConfig = configUpdater(formik.values.config);
      formik.setFieldValue('config', newConfig);
    } else {
      // If it's a value, set it directly
      formik.setFieldValue('config', configUpdater);
    }
  }, [formik]);

  useEffect(() => {
    const fetchAssets = () => {
      getGeneralEQMAssetsByTypeForSku(user, formik.values.university, formik.values.type, { versionedOnly: true })
        .then((res) => {
          setGeneralAssetByType(res.data.obj);

          // Set options for different asset types
          const assetTypes = {
            "Font File": setFontOptions,
            "Back Asset Icon File": setBackIconLinkOptions,
            "Front Asset Icon File": setFrontIconLinkOptions,
            "Mockup Template File": setTemplateLinkOptions,
            "Composite Font File": setCompositeOptions,
            "Jersey Art File": setJerseyTemplateLinkOptions
          };

          Object.entries(assetTypes).forEach(([assetType, setter]) => {
            if (assetType in res.data.obj) {
              const keys = Object.values(res.data.obj[assetType]);
              if (assetType === "Composite Font File" || assetType === "Jersey Art File") {
                setter(keys.filter(key => key.tags.endsWith(EQM_TAG_VERSION_1_SUFFIX)));
              } else {
                setter(keys);
              }
            } else {
              setter([]);
            }
          });
        })
        .catch(err => notify("Failed to get assets: " + err));
    };

    fetchAssets();
    const interval = setInterval(fetchAssets, 10000);
    return () => clearInterval(interval);
  }, [user, notify, formik.values.university, formik.values.type]);



  useEffect(() => {
    getAllProductConfigurations(user)
      .then((response) => {
        setProductConfigurations(response.data);
        setLoading(false);
      })
      .catch((err) => {
        notify("Failed to get product configurations");
        setLoading(false);
      });
  }, [user, notify]);

  useEffect(() => {
    const controller = new AbortController();

    const config = {
      sku_name: formik.values.skuName,
      color: formik.values.color,
      type: formik.values.type,
      university: formik.values.university,
      style: formik.values.styleReference,
      color_reference: formik.values.colorReference,
      size_range: formik.values.sizeRange,
      athlete_id: formik.values.athleteId,
      sport: formik.values.sport,
      // approval_status: formik.values.approvalStatus ? 1 : null,
      vendor: formik.values.vendor,
      jersey_number: formik.values.jersey_number,
      gender: formik.values.gender,
      group: formik.values.group,
    };

    // console.log("pre", config);
    for (var field in config) {
      if (Object.prototype.hasOwnProperty.call(config, field)) {
        if (
          config[field] === undefined ||
          config[field] === null ||
          config[field] === ""
        ) {
          delete config[field];
        }
      }
    }
    if (config["jersey_number"][0] === -2) {
      delete config["jersey_number"];
    }

    // console.log("post", config);

    findProductMetadataMatching(config, controller.signal, user)
      .then((resp) => {
        setSimilarProducts(resp.data);
        // console.log(resp.data);
      })
      .catch((e) => {
        if (e.name !== "AbortError") {
          notify(e.response?.data);
        }
      });

    return () => {
      controller.abort();
    };
  }, [formik.values]);

  const validateForm = () => {
    let isValid = true;
    const invalidUniversities = new Set();

    const formikUniversities = formik.values.university || [];

    if (formikUniversities.length === 0) {
      isValid = false;
    } else {
      for (const university of formikUniversities) {
        const universityConfig = formik.values.config[university];
        if (!universityConfig) {
          invalidUniversities.add(university);
          isValid = false;
          break;
        }

        if (!universityConfig.mockup_template_general_asset_id ||
          universityConfig.mockup_template_general_asset_id === "") {
          invalidUniversities.add(university);
          isValid = false;
          break;
        }
      }
    }

    if (!isValid) {
      setErrorMessage("Please fill out all required fields.");
      setExpanded((prev) => {
        const newExpanded = { ...prev };
        invalidUniversities.forEach((university) => {
          newExpanded[university] = true;
        });
        return newExpanded;
      });
    } else {
      setErrorMessage("");
      setExpanded({});
    }

    return isValid;
  }

  useEffect(() => {
    setDisplayEQMConfigTab(
      formik.values.type && formik.values.type !== "Performance Tee" && formik.values.type !== "Performance Long Sleeve"
    );
  }, [formik.values.type])

  // Trigger validation when config changes
  useEffect(() => {
    if (displayEQMConfigTab && formik.values.config) {
      formik.validateField('config');
    }
  }, [formik.values.config, displayEQMConfigTab]);

  const typeVendorMap = {
    "Tee": "Influxer Merch",
    "Hoodie": "Influxer Merch",
    "Long Sleeve": "Influxer Merch",
    "Crewneck": "Influxer Merch",
    "Performance Tee": "Influxer Merch",
    "Performance Long Sleeve": "Influxer Merch",
    "Jersey": "Merch Fuel",
    "Coaster": "Velvet Impressions",
    "Tumbler": "Velvet Impressions",
    "Blanket": "Velvet Impressions",
    "Comfort Colors Tee": "Influxer Merch",
    "Garden Flag": "Velvet Impressions",
    "Can Cooler": "Velvet Impressions",
    "Tank Top": "Influxer Merch",
    "Bottle Opener": "Velvet Impressions",
    "Canvas Tote Bag": "Influxer Merch",
    "Polo": "Influxer Merch",
    "Quarter-Zip": "Influxer Merch",
    "Hat": "Influxer Merch",
    "Beanie": "Velvet Impressions",
    "Sweatpants": "Influxer Merch",
    "Clear Stadium Bag": "Velvet Impressions",
  };

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        alignContent: "center",
      }}
    >
      {loading && <LinearProgress />}
      <form onSubmit={formik.handleSubmit}>
        <TextField
          id="group"
          name="group"
          label="Group"
          variant="outlined"
          style={{ marginTop: "5vh" }}
          fullWidth
          disabled
          value={formik.values.group}
          error={formik.touched.group && Boolean(formik.errors.group)}
          helperText={formik.touched.group && formik.errors.group}
        />
        <AthleteSearch
          updateForm={(athleteId) =>
            formik.setFieldValue("athleteId", athleteId)
          }
        />
        {formik.touched.athleteId && formik.errors.athleteId && (
          <div>{formik.errors.athleteId}</div>
        )}
        <MultiSelect
          label="University"
          style={{ paddingTop: "10px" }}
          value={formik.values.university}
          onChange={(newValue) => {
            formik.setFieldValue("university", newValue);
          }}
          between={"OR"}
          onBlur={() => { }}
          options={[...universities]}
          getOptionLabel={(option) => option}
        />
        {formik.touched.university && formik.errors.university && (
          <ErrorText message={formik.errors.university} />
        )}
        <MultiSelect
          label="Gender"
          style={{ paddingTop: "10px" }}
          value={formik.values.gender}
          onChange={(newValue) => {
            formik.setFieldValue(
              "gender",
              newValue.length > 0 ? [newValue[newValue.length - 1]] : []
            );
          }}
          onBlur={() => { }}
          options={["Male", "Female"]}
          getOptionLabel={(option) => option}
          allButton
        />
        {formik.touched.gender && formik.errors.gender && (
          <ErrorText message={formik.errors.gender} />
        )}
        <MultiSelect
          label="Sport"
          style={{ paddingTop: "10px" }}
          value={formik.values.sport}
          onChange={(newValue) => {
            formik.setFieldValue("sport", newValue);
          }}
          between={"OR"}
          onBlur={formik.handleBlur}
          options={genderedSportList}
          getOptionLabel={(option) => option}
          allButton
        />
        {formik.touched.sport && formik.errors.sport && (
          <ErrorText message={formik.errors.sport} />
        )}
        <JerseyNumberSelector formik={formik} force={typeCheck} />

        <Autocomplete
          style={{ paddingTop: "15px" }}
          value={formik.values.color}
          onChange={(event, newValue) => {
            formik.setFieldValue("color", newValue);
            formik.setFieldValue(
              "skuName",
              buildSKU(newValue, formik.values.unique, formik.values.type)
            );
          }}
          onBlur={(a) => {
            formik.handleBlur(a);
            formik.setTouched({ ...formik.touched, color: true });
            formik.setFieldValue("color", a.target.value);
            formik.setFieldValue(
              "skuName",
              buildSKU(a.target.value, formik.values.unique, formik.values.type)
            );
          }}
          options={
            productConfiguration ? Object.keys(productConfiguration) : []
          }
          getOptionLabel={(option) => option}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Color"
              variant="outlined"
              style={{ width: "100%" }}
              InputProps={{
                ...params.InputProps,
              }}
              type="text"
              error={formik.touched.color && Boolean(formik.errors.color)}
              helperText={formik.touched.color && formik.errors.color}
            />
          )}
        />
        <TextField
          id="unique"
          name="unique"
          label="Unique"
          variant="outlined"
          style={{ marginTop: "10px" }}
          fullWidth
          onChange={(e, newValue) => {
            formik.setFieldValue("unique", newValue);
            formik.setFieldValue(
              "skuName",
              buildSKU(formik.values.color, newValue, formik.values.type)
            );
          }}
          onBlur={(e, f) => {
            formik.handleBlur(e, f);
            formik.setFieldValue("unique", e.target.value);
            formik.setFieldValue(
              "skuName",
              buildSKU(formik.values.color, e.target.value, formik.values.type)
            );
          }}
          value={formik.values.unique}
          error={formik.touched.unique && Boolean(formik.errors.unique)}
          helperText={formik.touched.unique && formik.errors.unique}
        />
        <Autocomplete
          style={{ paddingTop: "15px" }}
          value={formik.values.type}
          onChange={(event, newValue) => {
            formik.setFieldValue("type", newValue);
            formik.setFieldValue(
              "skuName",
              buildSKU(formik.values.color, formik.values.unique, newValue)
            );

            setTypeCheck(false);

            if (typeVendorMap[newValue]) {
              formik.setFieldValue("vendor", typeVendorMap[newValue]);
            }

          }}
          onBlur={(a) => {
            formik.handleBlur(a);
            formik.setTouched({ ...formik.touched, type: true });
            formik.setFieldValue("type", a.target.value);
            formik.setFieldValue(
              "skuName",
              buildSKU(
                formik.values.color,
                formik.values.unique,
                a.target.value
              )
            );

            if (typeVendorMap[newValue]) {
              formik.setFieldValue("vendor", typeVendorMap[newValue]);
            }
          }}
          options={GetColorOptions(productConfiguration, formik.values.color)}
          getOptionLabel={(option) => option}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Type"
              variant="outlined"
              style={{ width: "100%" }}
              InputProps={{
                ...params.InputProps,
              }}
              type="text"
              error={formik.touched.type && Boolean(formik.errors.type)}
              helperText={formik.touched.type && formik.errors.type}
            />
          )}
        />
        <TextField
          id="skuName"
          name="skuName"
          label="SKU Name"
          variant="outlined"
          disabled
          style={{ marginTop: "20px" }}
          fullWidth
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.skuName}
          error={formik.touched.skuName && Boolean(formik.errors.skuName)}
          helperText={formik.touched.skuName && formik.errors.skuName}
        />
        {GetConfiguration(
          productConfiguration,
          formik.values.type,
          formik.values.color,
          selectedProductConfiguration,
          setSelectedProductConfiguration,
          (productConfiguration) => {

            formik.setFieldValue(
              "colorReference",
              productConfiguration.color_reference
            );
            formik.setFieldValue("sizeRange", productConfiguration.size_range);
            formik.setFieldValue(
              "styleReference",
              productConfiguration.style_reference
            );
            formik.setFieldTouched("sizeRange", true);
            formik.setFieldTouched("styleReference", true);
            formik.setFieldTouched("colorReference", true);
            formik.setFieldError("sizeRange", undefined);
            formik.setFieldError("styleReference", undefined);
            formik.setFieldError("colorReference", undefined);
          }
        )}
        <Autocomplete
          style={{ paddingTop: "15px" }}
          value={formik.values.vendor}
          onChange={(event, newValue) => {
            formik.setFieldValue("vendor", newValue);
          }}
          onBlur={(a) => {
            formik.handleBlur(a);
            formik.setTouched({ ...formik.touched, vendor: true });
            formik.setFieldValue("vendor", a.target.value);
          }}
          options={filterVendors(formik.values.type)}
          getOptionLabel={(option) => option}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vendor"
              variant="outlined"
              style={{ width: "100%" }}
              InputProps={{
                ...params.InputProps,
              }}
              type="text"
              error={formik.touched.vendor && Boolean(formik.errors.vendor)}
              helperText={formik.touched.vendor && formik.errors.vendor}
            />
          )}
        />
        <Tooltip
          title="Check this box if there's a school logo or mark on this item"
          arrow
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, -10],
                  },
                },
              ],
            },
          }}
        >
          <FormControlLabel
            style={{ marginTop: "20px" }}
            control={
              <Checkbox
                id="approvalStatus"
                name="approvalStatus"
                checked={formik.values.approvalStatus}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            }
            label="Royalty Bearing"
          />
        </Tooltip>
        <br />
        {formik.touched.approvalStatus && formik.errors.approvalStatus && (
          <ErrorText message={formik.errors.approvalStatus} />
        )}
        {formik.touched.colorReference && formik.errors.colorReference && (
          <ErrorText message={formik.errors.colorReference} />
        )}
        {formik.touched.sizeRange && formik.errors.sizeRange && (
          <ErrorText message={formik.errors.sizeRange} />
        )}
        {formik.touched.styleReference && formik.errors.styleReference && (
          <ErrorText message={formik.errors.styleReference} />
        )}
        {similarProducts.length > 0 && (
          <div style={{ margin: "10px 0px 15px 0px" }}>
            <MatchingSimilarProducts similarProducts={similarProducts} />
          </div>
        )}
        {/* EQM Congfig add here */}
        {displayEQMConfigTab && formik.values.university.length > 0 ? (
          <NewProductEQMConfig
            rows={rows}
            setRows={setRows}
            eqmConfig={formik.values.config}
            setEQMConfig={updateEQMConfig}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            universities={formik.values.university}
            expanded={expanded}
            setExpanded={setExpanded}
            fontOptions={fontOptions}
            backIconLinkOptions={backIconLinkOptions}
            frontIconLinkOptions={frontIconLinkOptions}
            templateLinkOptions={formik.values.type == "Jersey" ? jerseyTemplateLinkOptions : templateLinkOptions}
            compositeOptions={compositeOptions}
            generalAssetByType={generalAssetByType}
            selectedSkuType={formik.values.type}
          />
        ) : null}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading || (displayEQMConfigTab && !formik.isValid)}
          style={{ marginRight: "1rem" }}
        >
          Submit
        </Button>
        <Typography sx={{ color: "error.main" }}>
          Warning, this will create this product for ALL athletes that the,
          school, sport, athlete, jersey number, gender filter applies to.
        </Typography>
      </form>
    </div>
  );
};

export default CreateProductForm;
